import compact from 'lodash/compact'
import find from 'lodash/find'
import isBoolean from 'lodash/isBoolean'
import moment from 'moment'
import { TAppointmentStatus } from 'transfix-ui/components/AppointmentTag/types'
import { TSelectOpt } from 'transfix-ui/components/Select/SelectTypes'
import {
  IEquipmentTypes,
  IFacilityRequirements,
  IFacilityScheduleRulesUnloadTypes,
  IFacilityServices,
  IShipmentStopAppointmentStatuses,
  IShipmentStopAppointmentTypes,
  IUpdateShipmentStopInput,
  IUpdateShipmentStopsInput,
  Maybe,
  IRescheduleReasons,
} from 'types/graphqlTypes'
import FORMATS from 'constants/datetime'
import { isDateDifferent, isTimeDifferent } from '../ShipmentSchedulingTab/utils'
import { TEditAppointmentSchemaType } from './EditAppointments/EditAppointmentSchema'
import { TStopMarkerPercentage } from './StopMarkerIcon/types'
import { IShipmentProgressStopFragment } from './graphql/ShipmentProgressStopFragment'

/**
 * Returns percentage complete based on arrival and departure values
 */
export const getStopMarkerPercentage = (
  args: Pick<IShipmentProgressStopFragment, 'arrivedAt' | 'leftAt'>
): TStopMarkerPercentage => {
  const { arrivedAt, leftAt } = args || {}
  let percentage: TStopMarkerPercentage = 0
  if (leftAt && arrivedAt) {
    percentage = 100
  } else if (arrivedAt) {
    percentage = 50
  }
  return percentage
}

interface IGetStopStatusStop {
  appointment: Pick<
    IShipmentProgressStopFragment['appointment'],
    'startAt' | 'startAtHasTime' | 'status'
  >
  rescheduleRequest?: Maybe<
    Pick<NonNullable<IShipmentProgressStopFragment['rescheduleRequest']>, 'isWorkInRequested'>
  >
}
/**
 * Returns a status for tag based on status and isWorkIn
 */
export const getStopStatus = (stop: IGetStopStatusStop): TAppointmentStatus => {
  const { appointment, rescheduleRequest } = stop || {}
  const { isWorkInRequested } = rescheduleRequest || {}
  const { startAt, startAtHasTime, status } = appointment || {}

  let appointmentStatus: TAppointmentStatus = 'no-appointment'

  if (!startAtHasTime) {
    appointmentStatus = 'no-appointment'
  } else if (!isWorkInRequested && !!rescheduleRequest) {
    appointmentStatus = 'reschedule-requested-open'
  } else if (isWorkInRequested) {
    appointmentStatus = 'work-in-request-open'
  } else if (status === IShipmentStopAppointmentStatuses.confirmed) {
    appointmentStatus = 'confirmed'
  } else if (status === IShipmentStopAppointmentStatuses.unconfirmed || !startAt) {
    appointmentStatus = 'unconfirmed'
  } else if (status === IShipmentStopAppointmentStatuses.predicted) {
    appointmentStatus = 'predicted'
  }

  return appointmentStatus
}

/**
 * Based on whether a stop is the first, last, or middle child of
 * the parent div, the flexBasis value will change.
 */
export const getFlexBasis = (totalStops: number, isBookend: boolean): string => {
  const dividedPercentage = 100 / totalStops

  // Divide 100 by total stops
  if (isBookend) {
    const spacesBetweenStopsCount = totalStops - 1
    const halfSpacesBetweenStopsCount = spacesBetweenStopsCount * 2
    return `${(dividedPercentage * halfSpacesBetweenStopsCount) / 10}%`
  }

  // i.e. 4 stops is 100 / 4 or 25%
  return `${dividedPercentage}%`
}

type TItems = IEquipmentTypes | IFacilityServices | IFacilityRequirements

export const getMetadataDetailsField = (items: Maybe<TItems[]>, dictionary: TSelectOpt[]) => {
  if (!items?.length || !dictionary) {
    return ''
  }
  const allItems = items.map(item => find(dictionary, { value: item })?.label || item)
  return compact(allItems).join(', ')
}

export const getFacilitySchedulingRule = (stop?: IShipmentProgressStopFragment) => {
  const { dropTrailer, mergedFacility, stopType } = stop || {}
  const { schedule } = mergedFacility || {}
  const unloadType = dropTrailer
    ? IFacilityScheduleRulesUnloadTypes.DROP
    : IFacilityScheduleRulesUnloadTypes.LIVE

  return schedule?.rules.value.find(
    ({ stopType: ruleStopType, unloadType: ruleUnloadType }) =>
      ruleStopType === stopType && ruleUnloadType === unloadType
  )
}

const getAppointmentStatus = (
  status: boolean | undefined,
  currentStatus?: Maybe<IShipmentStopAppointmentStatuses>
): IShipmentStopAppointmentStatuses | undefined => {
  if (!isBoolean(status)) {
    return status
  }

  if (status) {
    return IShipmentStopAppointmentStatuses.confirmed
  }

  if (currentStatus === IShipmentStopAppointmentStatuses.confirmed) {
    return IShipmentStopAppointmentStatuses.unconfirmed
  }

  return currentStatus || IShipmentStopAppointmentStatuses.unconfirmed
}

const dateTimeISO8601Timezone = (inputDateTime: string, timezone: string) => {
  // Avoids moment localization
  const inputDateTimeISO = new Date(inputDateTime).toISOString()

  return moment.tz(inputDateTimeISO, timezone).format(FORMATS.yearFirst)
}

const getUpdateShipmentRequest = (
  stop: Maybe<IShipmentProgressStopFragment>,
  formInput: Partial<TEditAppointmentSchemaType>
): IUpdateShipmentStopInput => {
  const { appointment, id = '' } = stop || {}
  const { endAt, startAt, timezone = FORMATS.defaultTimeZone, status: currentStatus } =
    appointment || {}

  const { confirmed } = formInput || {}
  const appointmentStatus = getAppointmentStatus(confirmed, currentStatus)
  const rescheduleReason = formInput.rescheduleReason as IRescheduleReasons

  const input: IUpdateShipmentStopInput = {
    id,
    dropTrailer: formInput.dropTrailer,
    specialInstruction: formInput.stopSpecialInstruction || '',
    weight: formInput.weight,
    palletCount: formInput.palletCount,
    commodity: formInput.commodity,
    genericCommodityId: formInput.genericCommodityId,
    poReference: formInput.poReference,
    billOfLadingReference: formInput.billOfLadingReference,
    ediAppointmentReasonCode: formInput.ediAppointmentReasonCode,
    ediStatusReasonCode: formInput.ediStatusReasonCode,
    shipperFacilityUuid: formInput.facility?.id,
    appointmentInput: {
      isWorkIn: formInput.isWorkIn,
      rescheduleReason: rescheduleReason as IRescheduleReasons | undefined,
      appointmentReference: formInput.appointmentReference || '',
      confirmationReference: formInput.confirmationReference || '',
      appointmentType: formInput.appointmentType as IShipmentStopAppointmentTypes | undefined,
      status: appointmentStatus,
      startAtTime: undefined,
      startAtDate: undefined,
      endAtTime: undefined,
      endAtDate: undefined,
    },
    vendorId: formInput.vendorId,
  }

  const isStartAtTimeDifferent = isTimeDifferent(startAt, formInput.startAtTime, timezone)
  const isStartAtDateDifferent = isDateDifferent(startAt, formInput.startAtDate, timezone)
  const isEndAtTimeDifferent = isTimeDifferent(endAt, formInput.endAtTime, timezone)
  const isEndAtDateDifferent = isDateDifferent(endAt, formInput.endAtDate, timezone)

  if (!input.appointmentInput) {
    return input
  }

  if (isStartAtTimeDifferent) {
    input.appointmentInput.startAtTime = formInput.startAtTime || null
  }

  if (isStartAtDateDifferent) {
    input.appointmentInput.startAtDate = formInput.startAtDate
      ? dateTimeISO8601Timezone(formInput.startAtDate, timezone)
      : null
  }

  if (formInput.appointmentType === 'range') {
    if (isEndAtTimeDifferent) {
      input.appointmentInput.endAtTime = formInput.endAtTime || null
    }
    if (isEndAtDateDifferent) {
      input.appointmentInput.endAtDate = formInput.endAtDate
        ? dateTimeISO8601Timezone(formInput.endAtDate, timezone)
        : null
    }
  }

  if (rescheduleReason) {
    input.appointmentInput.status = getAppointmentStatus(confirmed)
  }

  return input
}

export const getUpdateShipmentStopsRequest = (
  stops: IShipmentProgressStopFragment[],
  formValues: Partial<TEditAppointmentSchemaType>[]
): IUpdateShipmentStopsInput => {
  const shipmentStops = stops.map((stop, index) =>
    getUpdateShipmentRequest(stop, formValues[index])
  )

  return {
    shipmentStops,
  }
}
